if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.dialog = this.querySelector('.quick-add-modal__content');
        this.fitDialogToViewport = this.fitDialogToViewport.bind(this);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        window.removeEventListener('resize', this.fitDialogToViewport);
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.mobileVariantCarouselUnsubscriber) {
          this.mobileVariantCarouselUnsubscriber();
          this.mobileVariantCarouselUnsubscriber = null;
        }
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();

        if (Number.isFinite(this.catalogueScrollPosition)) {
          const scrollPosition = this.catalogueScrollPosition;
          window.requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
        }
      }

      show(opener) {
        this.catalogueScrollPosition = window.scrollY;
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        const variantImageUrl = opener.dataset.variantImageUrl;
        if (variantImageUrl) {
          const variantImagePreload = new Image();
          variantImagePreload.fetchPriority = 'high';
          variantImagePreload.src = variantImageUrl;
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            const nativeGallery = this.modalContent.querySelector('media-gallery');
            if (nativeGallery?.querySelector('.thumbnail-slider')) {
              nativeGallery.classList.add('hs-has-native-thumbnails');
            }
            this.initializeDeliveryTimeline();
            this.initializeVariantPreview(opener);
            this.initializeMediaCarousel(opener);
            this.initializeMobileControls();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.fitDialogToViewport();
            window.requestAnimationFrame(this.fitDialogToViewport);
            window.addEventListener('resize', this.fitDialogToViewport, { passive: true });
          })
          .catch((error) => {
            console.error('Unable to open product options', error);
            const productUrl = opener.getAttribute('data-product-url');
            if (productUrl) window.location.assign(productUrl);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      fitDialogToViewport() {
        if (!this.dialog || !this.hasAttribute('open')) return;

        const properties = ['top', 'bottom', 'left', 'width', 'height', 'max-width', 'max-height', 'margin', 'overflow', 'transform', 'transform-origin'];
        if (window.innerWidth < 750) {
          properties.forEach((property) => this.dialog.style.removeProperty(property));
          return;
        }

        const baseWidth = 1200;
        const baseHeight = 830;
        const edgeGap = 24;
        const header = document.querySelector('.section-header, .shopify-section-header-sticky, sticky-header');
        const measuredHeaderBottom = header?.getBoundingClientRect().bottom || 0;
        const headerBottom = Math.max(0, Math.min(140, measuredHeaderBottom));
        const top = headerBottom + edgeGap;
        const availableWidth = Math.max(1, window.innerWidth - edgeGap * 2);
        const availableHeight = Math.max(1, window.innerHeight - top - edgeGap);
        const scale = Math.min(1, availableWidth / baseWidth, availableHeight / baseHeight);
        const set = (property, value) => this.dialog.style.setProperty(property, value, 'important');

        set('top', `${Math.round(top)}px`);
        set('bottom', 'auto');
        set('left', '50%');
        set('width', `${baseWidth}px`);
        set('height', `${baseHeight}px`);
        set('max-width', 'none');
        set('max-height', 'none');
        set('margin', '0');
        set('overflow', 'hidden');
        set('transform-origin', 'top center');
        set('transform', `translateX(-50%) scale(${scale})`);
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      initializeDeliveryTimeline() {
        this.modalContent.querySelectorAll('[data-delivery-timeline]').forEach((timeline) => {
          const form = timeline.parentElement?.querySelector('product-form');
          const submitButton = form?.querySelector('.product-form__submit');
          const formatDate = (date) => date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
          const addDays = (date, days) => {
            const next = new Date(date);
            next.setDate(next.getDate() + Number(days || 0));
            return next;
          };
          const formatRange = (start, end) =>
            start.getTime() === end.getTime() ? formatDate(start) : `${formatDate(start)} - ${formatDate(end)}`;
          const today = new Date();
          const readyMin = Number(timeline.dataset.readyMin || 0);
          const readyMax = Math.max(readyMin, Number(timeline.dataset.readyMax || readyMin));
          const deliveryMin = Number(timeline.dataset.deliveryMin || 0);
          const deliveryMax = Math.max(deliveryMin, Number(timeline.dataset.deliveryMax || deliveryMin));
          const updateVisibility = () => {
            timeline.hidden = Boolean(submitButton?.disabled);
          };

          timeline.querySelector('[data-delivery-ordered-date]').textContent = formatDate(today);
          timeline.querySelector('[data-delivery-ready-date]').textContent = formatRange(
            addDays(today, readyMin),
            addDays(today, readyMax)
          );
          timeline.querySelector('[data-delivery-delivered-date]').textContent = formatRange(
            addDays(today, deliveryMin),
            addDays(today, deliveryMax)
          );

          if (submitButton) {
            new MutationObserver(updateVisibility).observe(submitButton, {
              attributes: true,
              attributeFilter: ['disabled', 'aria-disabled'],
            });
          }
          updateVisibility();
        });
      }

      initializeMobileControls() {
        const contentInfo = this.modalContent;

        if (contentInfo) {
          window.requestAnimationFrame(() => {
            contentInfo.scrollTop = 0;
            window.requestAnimationFrame(() => {
              contentInfo.scrollTop = 0;
            });
          });
        }
      }

      initializeVariantPreview(opener) {
        if (!window.matchMedia('(max-width: 989px)').matches) return;

        const gallery = this.modalContent.querySelector('media-gallery');
        const variantImageUrl = opener.dataset.variantImageUrl;
        if (!gallery || !variantImageUrl) return;

        const preview = document.createElement('div');
        preview.className = 'hs-mobile-variant-preview';
        const image = document.createElement('img');
        image.src = variantImageUrl;
        image.alt = '';
        image.loading = 'eager';
        image.fetchPriority = 'high';
        preview.appendChild(image);
        gallery.classList.add('hs-has-variant-preview');
        gallery.prepend(preview);

        const viewer = gallery.querySelector('[id^="GalleryViewer"]');
        if (viewer) viewer.style.setProperty('display', 'none', 'important');
      }

      initializeMediaCarousel(opener) {
        const gallery = this.modalContent.querySelector('media-gallery');
        const initialImage = gallery?.querySelector('.product__media-item.is-active img, .product__media-item img');
        const productUrl = opener.getAttribute('data-product-url');
        if (!gallery || !initialImage || !productUrl) return;

        const jsonUrl = new URL(productUrl, window.location.origin);
        jsonUrl.pathname = `${jsonUrl.pathname.replace(/\/$/, '')}.js`;

        fetch(jsonUrl.toString())
          .then((response) => response.json())
          .then((product) => {
            const media = (product.media || []).filter(
              (item) => item.media_type === 'image' && item.preview_image?.src
            );
            if (media.length < 2) return;

            if (window.matchMedia('(max-width: 989px)').matches) {
              const viewer = gallery.querySelector('[id^="GalleryViewer"]');
              if (!viewer) return;

              const initialSource = initialImage.currentSrc || initialImage.src || '';
              const cardVariantSource = opener.dataset.variantImageUrl || '';
              const cardVariantMediaIndex = media.findIndex((item) => {
                const mediaSource = item.preview_image.src.split('?')[0];
                return cardVariantSource.includes(mediaSource);
              });
              const requestedVariantId = Number(jsonUrl.searchParams.get('variant'));
              const requestedVariant = (product.variants || []).find(
                (variant) => Number(variant.id) === requestedVariantId
              );
              const requestedMediaId = Number(
                requestedVariant?.featured_media?.id || requestedVariant?.featured_image?.id
              );
              const requestedMediaSource =
                requestedVariant?.featured_media?.preview_image?.src || requestedVariant?.featured_image?.src || '';
              const requestedMediaIndex = media.findIndex((item) => {
                if (requestedMediaId && Number(item.id) === requestedMediaId) return true;
                if (!requestedMediaSource) return false;
                return requestedMediaSource.includes(item.preview_image.src.split('?')[0]);
              });
              const renderedMediaIndex = media.findIndex((item) =>
                initialSource.includes(item.preview_image.src.split('?')[0])
              );
              const selectedMediaIndex =
                cardVariantMediaIndex !== -1
                  ? cardVariantMediaIndex
                  : requestedMediaIndex !== -1
                    ? requestedMediaIndex
                    : Math.max(0, renderedMediaIndex);
              const mobileMedia =
                selectedMediaIndex > 0
                  ? [...media.slice(selectedMediaIndex), ...media.slice(0, selectedMediaIndex)]
                  : media;
              let mobileIndex = 0;
              let mobileCarouselReady = false;

              const carousel = document.createElement('div');
              carousel.className = 'hs-mobile-media-carousel';
              carousel.setAttribute('aria-label', 'Product images');

              const track = document.createElement('div');
              track.className = 'hs-mobile-media-carousel__track';
              mobileMedia.forEach((item, index) => {
                const slide = document.createElement('div');
                slide.className = 'hs-mobile-media-carousel__slide';
                slide.setAttribute('aria-label', `Image ${index + 1} of ${media.length}`);
                const slideImage = document.createElement('img');
                slideImage.src =
                  index === 0 && opener.dataset.variantImageUrl
                    ? opener.dataset.variantImageUrl
                    : item.preview_image.src;
                slideImage.alt = item.alt || product.title || '';
                slideImage.loading = index === mobileIndex ? 'eager' : 'lazy';
                if (index === mobileIndex) slideImage.fetchPriority = 'high';
                slide.appendChild(slideImage);
                track.appendChild(slide);
              });

              const navigation = document.createElement('div');
              navigation.className = 'hs-mobile-media-carousel__navigation';
              navigation.innerHTML = `
                <button type="button" aria-label="Previous image"><span aria-hidden="true">&#8249;</span></button>
                <span class="hs-mobile-media-carousel__counter" aria-live="polite">1 / ${mobileMedia.length}</span>
                <button type="button" aria-label="Next image"><span aria-hidden="true">&#8250;</span></button>
              `;

              const counter = navigation.querySelector('.hs-mobile-media-carousel__counter');
              const goToMobileImage = (index, behavior = 'smooth') => {
                mobileIndex = (index + mobileMedia.length) % mobileMedia.length;
                track.scrollTo({ left: mobileIndex * track.clientWidth, behavior });
                counter.textContent = `${mobileIndex + 1} / ${mobileMedia.length}`;
              };

              navigation.firstElementChild.addEventListener('click', () => goToMobileImage(mobileIndex - 1));
              navigation.lastElementChild.addEventListener('click', () => goToMobileImage(mobileIndex + 1));

              let scrollFrame;
              track.addEventListener(
                'scroll',
                () => {
                  if (!mobileCarouselReady) return;
                  window.cancelAnimationFrame(scrollFrame);
                  scrollFrame = window.requestAnimationFrame(() => {
                    if (!track.clientWidth) return;
                    mobileIndex = Math.round(track.scrollLeft / track.clientWidth);
                    counter.textContent = `${mobileIndex + 1} / ${mobileMedia.length}`;
                  });
                },
                { passive: true }
              );

              carousel.appendChild(track);
              carousel.appendChild(navigation);
              carousel.style.visibility = 'hidden';
              gallery.classList.add('hs-has-mobile-media-carousel');
              gallery.insertBefore(carousel, viewer);
              const selectedSlideImage = track.firstElementChild?.querySelector('img');
              const revealCarousel = () => {
                gallery.classList.remove('hs-has-variant-preview');
                window.requestAnimationFrame(() => {
                  mobileCarouselReady = true;
                  carousel.style.visibility = 'visible';
                  gallery.querySelector('.hs-mobile-variant-preview')?.remove();
                });
              };
              if (selectedSlideImage?.complete) {
                revealCarousel();
              } else {
                selectedSlideImage?.addEventListener('load', revealCarousel, { once: true });
                selectedSlideImage?.addEventListener('error', revealCarousel, { once: true });
              }

              if (this.mobileVariantCarouselHandler) {
                this.modalContent.removeEventListener('change', this.mobileVariantCarouselHandler);
              }
              let mobileVariantUserInteraction = false;
              this.modalContent.addEventListener(
                'pointerdown',
                (event) => {
                  if (event.target.closest('variant-selects, variant-radios, .product-form__input')) {
                    mobileVariantUserInteraction = true;
                  }
                },
                { capture: true }
              );

              this.mobileVariantCarouselHandler = (event) => {
                if (!event.isTrusted) return;
                window.setTimeout(() => {
                  const activeVariantImage = gallery.querySelector('.product__media-item.is-active img');
                  const activeSource = activeVariantImage?.currentSrc || activeVariantImage?.src || '';
                  const variantIndex = mobileMedia.findIndex((item) =>
                    activeSource.includes(item.preview_image.src.split('?')[0])
                  );
                  if (variantIndex !== -1) goToMobileImage(variantIndex);
                }, 100);
              };
              this.modalContent.addEventListener('change', this.mobileVariantCarouselHandler);

              if (this.mobileVariantCarouselUnsubscriber) {
                this.mobileVariantCarouselUnsubscriber();
              }
              if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                this.mobileVariantCarouselUnsubscriber = subscribe(
                  PUB_SUB_EVENTS.variantChange,
                  ({ data }) => {
                    if (
                      !this.hasAttribute('open') ||
                      !mobileVariantUserInteraction ||
                      !data?.variant?.featured_media?.id
                    ) return;

                    const featuredMediaId = Number(data.variant.featured_media.id);
                    const variantIndex = mobileMedia.findIndex((item) => Number(item.id) === featuredMediaId);
                    if (variantIndex !== -1) {
                      window.requestAnimationFrame(() => goToMobileImage(variantIndex, 'auto'));
                    }
                    mobileVariantUserInteraction = false;
                  }
                );
              }
              return;
            }

            let activeIndex = Math.max(
              0,
              media.findIndex((item) => initialImage.currentSrc.includes(item.preview_image.src.split('?')[0]))
            );
            gallery.querySelector('.hs-gallery-nav')?.remove();

            const thumbnails = document.createElement('div');
            thumbnails.className = 'hs-quick-add-thumbnails';
            thumbnails.setAttribute('aria-label', 'Choose product image');
            media.forEach((item, index) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.className = `hs-quick-add-thumbnail${index === activeIndex ? ' is-active' : ''}`;
              button.setAttribute('aria-label', `View image ${index + 1}`);
              const thumbnailImage = document.createElement('img');
              thumbnailImage.src = item.preview_image.src;
              thumbnailImage.alt = item.alt || '';
              thumbnailImage.loading = 'lazy';
              button.appendChild(thumbnailImage);
              button.addEventListener('click', () => showImage(index));
              thumbnails.appendChild(button);
            });

            const navigation = document.createElement('div');
            navigation.className = 'hs-gallery-nav hs-quick-add-gallery-nav';
            navigation.setAttribute('aria-label', 'Product image navigation');
            navigation.innerHTML = `
              <button type="button" class="hs-gallery-nav__button hs-gallery-nav__button--prev" aria-label="Previous image">
                <span aria-hidden="true">&#8249;</span>
              </button>
              <span class="hs-gallery-nav__counter" aria-live="polite">${activeIndex + 1} / ${media.length}</span>
              <button type="button" class="hs-gallery-nav__button hs-gallery-nav__button--next" aria-label="Next image">
                <span aria-hidden="true">&#8250;</span>
              </button>
            `;

            const counter = navigation.querySelector('.hs-gallery-nav__counter');
            let imageTransitioning = false;

            const showImage = (index) => {
              activeIndex = (index + media.length) % media.length;
              const selected = media[activeIndex];
              const image = gallery.querySelector('.product__media-item.is-active img, .product__media-item img');
              if (!image) return;
              image.removeAttribute('srcset');
              image.src = selected.preview_image.src;
              image.alt = selected.alt || product.title || '';
              if (counter) counter.textContent = `${activeIndex + 1} / ${media.length}`;
              thumbnails.querySelectorAll('.hs-quick-add-thumbnail').forEach((thumbnail, thumbnailIndex) => {
                thumbnail.classList.toggle('is-active', thumbnailIndex === activeIndex);
              });
            };

            const getDisplayedImageIndex = () => {
              const displayedImage = gallery.querySelector('.product__media-item.is-active img, .product__media-item img');
              if (!displayedImage) return activeIndex;

              const displayedSource = displayedImage.currentSrc || displayedImage.src || '';
              const displayedIndex = media.findIndex((item) =>
                displayedSource.includes(item.preview_image.src.split('?')[0])
              );
              return displayedIndex === -1 ? activeIndex : displayedIndex;
            };

            const transitionToImage = (index, direction) => {
              if (imageTransitioning) return;
              const displayedImage = gallery.querySelector('.product__media-item.is-active img, .product__media-item img');
              if (!displayedImage || window.matchMedia('(min-width: 990px)').matches) {
                showImage(index);
                return;
              }

              imageTransitioning = true;
              const outClass = direction > 0 ? 'hs-media-swipe-out-left' : 'hs-media-swipe-out-right';
              const inClass = direction > 0 ? 'hs-media-swipe-in-right' : 'hs-media-swipe-in-left';
              displayedImage.classList.add(outClass);

              window.setTimeout(() => {
                showImage(index);
                displayedImage.classList.remove(outClass);
                displayedImage.classList.add(inClass);
                window.setTimeout(() => {
                  displayedImage.classList.remove(inClass);
                  imageTransitioning = false;
                }, 220);
              }, 150);
            };

            navigation.querySelector('.hs-gallery-nav__button--prev').addEventListener('click', () => {
              transitionToImage(getDisplayedImageIndex() - 1, -1);
            });
            navigation.querySelector('.hs-gallery-nav__button--next').addEventListener('click', () => {
              transitionToImage(getDisplayedImageIndex() + 1, 1);
            });

            const viewer = gallery.querySelector('[id^="GalleryViewer"]');
            let touchStartX = 0;
            let touchStartY = 0;
            let touchAxis = null;

            viewer?.addEventListener(
              'touchstart',
              (event) => {
                const touch = event.changedTouches[0];
                touchStartX = touch.clientX;
                touchStartY = touch.clientY;
                touchAxis = null;
              },
              { passive: true }
            );
            viewer?.addEventListener(
              'touchmove',
              (event) => {
                const touch = event.changedTouches[0];
                const deltaX = touch.clientX - touchStartX;
                const deltaY = touch.clientY - touchStartY;

                if (!touchAxis && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
                  touchAxis = Math.abs(deltaX) > Math.abs(deltaY) * 1.15 ? 'horizontal' : 'vertical';
                }
                if (touchAxis === 'horizontal') event.preventDefault();
              },
              { passive: false }
            );
            viewer?.addEventListener(
              'touchend',
              (event) => {
                if (window.matchMedia('(min-width: 990px)').matches) return;
                const touch = event.changedTouches[0];
                const deltaX = touch.clientX - touchStartX;
                const deltaY = touch.clientY - touchStartY;
                if (touchAxis !== 'horizontal' || Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
                const direction = deltaX < 0 ? 1 : -1;
                transitionToImage(getDisplayedImageIndex() + direction, direction);
              },
              { passive: true }
            );

            gallery.classList.add('hs-has-quick-add-thumbnails');
            gallery.insertBefore(thumbnails, viewer);
            viewer?.appendChild(navigation);
          })
          .catch(() => {});
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
